import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth';
import AuthPageLayout from '../components/ui/AuthPageLayout';
import AlertMessage from '../components/ui/AlertMessage';
import AuthLink from '../components/ui/AuthLink';

const ConfirmEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isConfirming, setIsConfirming] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid confirmation link. No token provided.');
        setIsConfirming(false);
        return;
      }

      try {
        const response = await authService.confirmEmail({ token });
        setError(''); // Clear any error
        setSuccess(
          response.message ||
            'Email confirmed successfully! You can now sign in to your account.'
        );
        setIsConfirming(false);
        // Redirect to sign in after 3 seconds
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          'Email confirmation failed. The link may be invalid or expired.';
        setSuccess(''); // Clear any success
        setError(errorMessage);
        setIsConfirming(false);
      }
    };

    confirmEmail();
  }, [searchParams, navigate]);

  return (
    <AuthPageLayout
      title={
        isConfirming
          ? 'Confirming your email...'
          : success
            ? 'Email Confirmed!'
            : 'Confirmation Failed'
      }
      description={
        !isConfirming && !success ? (
          <>
            Need a new confirmation link?{' '}
            <AuthLink to="/signup">Sign up again</AuthLink>
          </>
        ) : undefined
      }
    >
      <div className="space-y-6">
        {isConfirming && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
          </div>
        )}

        <AlertMessage type="error" message={error} show={!!error} />
        <AlertMessage type="success" message={success} show={!!success} />

        {success && (
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Redirecting to sign in page...
          </p>
        )}

        {error && (
          <div className="text-center">
            <AuthLink to="/signin">Go to Sign In</AuthLink>
          </div>
        )}
      </div>
    </AuthPageLayout>
  );
};

export default ConfirmEmail;
